/**
 * Industry Insights Dashboard
 *
 * Access Control:
 * - System admins (from system_admins table) have automatic access
 * - Specific users can be granted access via the industry_insights_access table
 *
 * To grant access to a specific user (e.g., Salesforce Ben):
 * INSERT INTO industry_insights_access (email, is_active, notes, granted_by)
 * VALUES ('ben@salesforce.com', true, 'Salesforce thought leader partnership', 'john@flowlogicllc.com');
 *
 * To revoke access:
 * UPDATE industry_insights_access SET is_active = false WHERE email = 'user@example.com';
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../services/supabaseClient';
import Navbar from '../../components/Navbar';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import {
  ChartBarIcon,
  DocumentChartBarIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

// System administrators
const SYSTEM_ADMINS = ['john@flowlogicllc.com'];

const COLORS = {
  A: '#10b981', // green
  B: '#3b82f6', // blue
  C: '#f59e0b', // amber
  D: '#f97316', // orange
  F: '#ef4444', // red
  I: '#6b7280', // gray
};

const ISSUE_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e'];

interface IndustryInsights {
  totalOrgs: number;
  totalFlows: number;
  gradeDistribution: Record<string, { count: number; percentage: string }>;
  averageGrade: { numeric: string; letter: string } | null;
  issueDistribution: Record<string, { count: number; percentage: string }>;
  flowTypeDistribution: Record<string, { count: number; percentage: string }>;
  orgSizeDistribution: Record<string, { count: number; percentage: string; label: string }>;
  averageFlowsPerOrg: number;
  orgStats: {
    minFlows: number;
    maxFlows: number;
    avgFlows: string;
    avgOrgGrade: string | null;
  };
  orgFilter: string;
}

export default function IndustryInsightsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [insights, setInsights] = useState<IndustryInsights | null>(null);
  const [orgFilter, setOrgFilter] = useState<'all' | 'production' | 'sandbox'>('all');

  // Check authorization
  useEffect(() => {
    const checkAuth = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      // Check if system admin
      const isSystemAdmin = SYSTEM_ADMINS.includes(user.email || '');
      if (isSystemAdmin) {
        setIsAuthorized(true);
        return;
      }

      // Check system_admins table
      const { data: adminData } = await supabase
        .from('system_admins')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();

      if (adminData) {
        setIsAuthorized(true);
        return;
      }

      // Check industry_insights_access table for specific user access
      const { data: accessData } = await supabase
        .from('industry_insights_access')
        .select('*')
        .eq('email', user.email)
        .eq('is_active', true)
        .maybeSingle();

      if (accessData) {
        setIsAuthorized(true);
      } else {
        navigate('/dashboard');
      }
    };

    checkAuth();
  }, [user, navigate]);

  // Fetch insights data
  useEffect(() => {
    if (!isAuthorized) return;

    const fetchInsights = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        const response = await fetch(
          `/api/admin/industry-insights?orgFilter=${orgFilter}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch insights');
        }

        const data = await response.json();
        setInsights(data);
      } catch (error) {
        console.error('Error fetching insights:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [isAuthorized, orgFilter]);

  if (!isAuthorized) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">Checking authorization...</p>
          </div>
        </div>
      </>
    );
  }

  if (loading || !insights) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading industry insights...</p>
          </div>
        </div>
      </>
    );
  }

  // Prepare chart data
  const gradeChartData = Object.entries(insights.gradeDistribution).map(([grade, data]) => ({
    grade,
    count: data.count,
    percentage: parseFloat(data.percentage)
  }));

  const issueChartData = Object.entries(insights.issueDistribution)
    .filter(([_, data]) => data.count > 0)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 10)
    .map(([issue, data]) => ({
      issue: formatIssueName(issue),
      count: data.count,
      percentage: parseFloat(data.percentage)
    }));

  const sizeChartData = Object.entries(insights.orgSizeDistribution).map(([size, data]) => ({
    size: data.label,
    count: data.count,
    percentage: parseFloat(data.percentage)
  }));

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ChartBarIcon className="h-8 w-8 mr-3 text-blue-600" />
              Industry Insights
            </h1>
            <p className="mt-2 text-gray-600">
              Real-world Flow health data across {insights.totalOrgs} connected organizations
            </p>
            <p className="text-sm text-gray-500 mt-1">
              For thought leadership and industry research • Access controlled
            </p>
          </div>

          {/* Filter Selector */}
          <div className="mb-6 flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Organization Filter:</label>
            <div className="flex space-x-2">
              {(['all', 'production', 'sandbox'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setOrgFilter(filter)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    orgFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricCard
              icon={<BuildingOfficeIcon className="h-6 w-6" />}
              title="Total Organizations"
              value={insights.totalOrgs.toLocaleString()}
              subtitle={`${orgFilter} orgs`}
              color="blue"
            />
            <MetricCard
              icon={<DocumentChartBarIcon className="h-6 w-6" />}
              title="Total Flows"
              value={insights.totalFlows.toLocaleString()}
              subtitle={`${insights.averageFlowsPerOrg.toFixed(1)} avg per org`}
              color="purple"
            />
            <MetricCard
              icon={<AcademicCapIcon className="h-6 w-6" />}
              title="Industry Avg Grade"
              value={insights.averageGrade?.letter || 'N/A'}
              subtitle={insights.averageGrade ? `${insights.averageGrade.numeric}/4.0` : ''}
              color={insights.averageGrade ? getGradeColor(insights.averageGrade.letter) : 'gray'}
            />
            <MetricCard
              icon={<ArrowTrendingUpIcon className="h-6 w-6" />}
              title="Best Practices"
              value={`${insights.gradeDistribution.A?.percentage || 0}%`}
              subtitle="Flows graded A"
              color="green"
            />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Grade Distribution */}
            <ChartCard title="Grade Distribution" subtitle="How flows are performing">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={gradeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => [`${value} flows`, 'Count']}
                    labelFormatter={(label) => `Grade ${label}`}
                  />
                  <Bar dataKey="count" fill="#3b82f6">
                    {gradeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.grade as keyof typeof COLORS]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 text-center text-sm text-gray-600">
                Industry average: <span className="font-semibold">{insights.averageGrade?.letter || 'N/A'}</span>
              </div>
            </ChartCard>

            {/* Org Size Distribution */}
            <ChartCard title="Organization Size Distribution" subtitle="By number of flows">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sizeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="size" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => [`${value} orgs`, 'Count']}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Issue Distribution - Full Width for better visibility */}
          <div className="mb-8">
            <ChartCard title="Most Common Issues" subtitle="Top anti-patterns detected across all flows">
              {issueChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={450}>
                  <BarChart data={issueChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="issue" type="category" width={200} />
                    <Tooltip
                      formatter={(value: number) => [`${value} flows`, 'Count']}
                    />
                    <Bar dataKey="count" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <div className="text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-lg font-medium">No issues detected</p>
                    <p className="text-sm mt-1">All flows are following best practices, or no flows have been analyzed yet.</p>
                  </div>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Insight Callouts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InsightCard
              title="Critical Issues"
              value={`${(parseFloat(insights.issueDistribution.QUERIES_IN_LOOPS?.percentage || '0') +
                      parseFloat(insights.issueDistribution.DML_OPERATIONS_IN_LOOPS?.percentage || '0')).toFixed(1)}%`}
              description="of flows have governor limit risks"
              trend="critical"
            />
            <InsightCard
              title="Technical Debt"
              value={`${(parseFloat(insights.gradeDistribution.D?.percentage || '0') +
                      parseFloat(insights.gradeDistribution.F?.percentage || '0')).toFixed(1)}%`}
              description="of flows need significant improvement"
              trend="warning"
            />
            <InsightCard
              title="Best in Class"
              value={`${insights.gradeDistribution.A?.percentage || 0}%`}
              description="of flows meet all best practices"
              trend="success"
            />
          </div>
        </div>
      </div>
    </>
  );
}

// Helper Components
function MetricCard({ icon, title, value, subtitle, color }: {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  color: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    amber: 'bg-amber-100 text-amber-600',
    gray: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${colorClasses[color as keyof typeof colorClasses] || colorClasses.gray} mb-4`}>
        {icon}
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </div>
  );
}

function ChartCard({ title, subtitle, children }: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function InsightCard({ title, value, description, trend }: {
  title: string;
  value: string;
  description: string;
  trend: 'critical' | 'warning' | 'success';
}) {
  const trendColors = {
    critical: 'border-red-200 bg-red-50',
    warning: 'border-amber-200 bg-amber-50',
    success: 'border-green-200 bg-green-50',
  };

  const valueColors = {
    critical: 'text-red-600',
    warning: 'text-amber-600',
    success: 'text-green-600',
  };

  return (
    <div className={`rounded-lg border-2 ${trendColors[trend]} p-6`}>
      <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
      <p className={`text-4xl font-bold ${valueColors[trend]} mb-2`}>{value}</p>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

// Helper functions
function formatIssueName(issue: string): string {
  return issue
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function getGradeColor(grade: string): string {
  const colors: Record<string, string> = {
    A: 'green',
    B: 'blue',
    C: 'amber',
    D: 'red',
    F: 'red',
  };
  return colors[grade] || 'gray';
}
